import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Button, 
  Space, 
  Typography, 
  Alert,
  Tag,
  Progress,
  message,
  Spin,
  Modal,
  Steps,
  Divider,
  List,
  Badge
} from 'antd';
import { 
  DollarOutlined, 
  CreditCardOutlined, 
  BankOutlined,
  QrcodeOutlined,
  CopyOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  ScanOutlined,
  UserOutlined,
  PhoneOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { Image } from 'antd';
import apiClient from '../utils/apiClient';
import websocketClient from '../utils/websocketClient';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const PaymentScannerPage = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector(state => state.auth);
  const [planData, setPlanData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState('pending'); // pending, processing, completed
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [paymentContent, setPaymentContent] = useState('');
  const [qrCodeLoaded, setQrCodeLoaded] = useState({});
  const qrCodeCache = useRef({});

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      try {
        // Load plan data first
        const planLoaded = await loadPlanData();
        if (!planLoaded) {
          navigate('/pricing');
          return;
        }
        
        // Load other data
        await loadUserData();
        await loadBankAccounts();
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadAllData();
  }, [planId]);

  useEffect(() => {
    if (paymentStatus === 'pending') {
      // Auto check payment every 5 seconds
      const interval = setInterval(() => {
        checkPaymentStatus();
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [paymentStatus]);

  // Update QR codes when payment content or plan data changes
  useEffect(() => {
    if (bankAccounts.length > 0 && paymentContent && planData) {
      const currentPaymentContent = paymentContent || (user ? `VOICE${user.id}` : 'VOICE4');
      
      const updatedAccounts = bankAccounts.map(account => ({
        ...account,
        qr_code: `https://img.vietqr.io/image/${account.bank_code}-${account.account_number}-compact.png?addInfo=${encodeURIComponent(currentPaymentContent)}&amount=${planData.price}`
      }));
      
      setBankAccounts(updatedAccounts);
      
      // Preload updated QR codes
      updatedAccounts.forEach(account => {
        preloadQRCode(account.qr_code, account.id);
      });
    }
  }, [paymentContent, planData]);

  // WebSocket connection for real-time payment notifications
  useEffect(() => {
    if (user && user.id) {
      // Connect to WebSocket
      websocketClient.connect(user.id);

      // Set up event listeners
      const handlePaymentSuccess = (data) => {
        console.log('Payment success received:', data);
        setPaymentStatus('completed');
        message.success(data.message || 'Payment successful! Your account has been upgraded.');
        // Refresh user data
        loadUserData();
        // Reset WebSocket connection
        websocketClient.disconnect();
        // Reconnect after a short delay
        setTimeout(() => {
          websocketClient.connect(user.id);
        }, 1000);
      };

      const handlePaymentProcessing = (data) => {
        console.log('Payment processing received:', data);
        setPaymentStatus('processing');
        message.info(data.message || 'Processing payment...');
      };

      const handleConnected = () => {
        console.log('WebSocket connected');
      };

      const handleDisconnected = () => {
        console.log('WebSocket disconnected');
      };

      const handleError = (error) => {
        console.error('WebSocket error:', error);
      };

      // Register event listeners
      websocketClient.onPaymentSuccess(handlePaymentSuccess);
      websocketClient.onPaymentProcessing(handlePaymentProcessing);
      websocketClient.onConnected(handleConnected);
      websocketClient.onDisconnected(handleDisconnected);
      websocketClient.onError(handleError);

      // Cleanup function
      return () => {
        websocketClient.off('payment_success', handlePaymentSuccess);
        websocketClient.off('payment_processing', handlePaymentProcessing);
        websocketClient.off('connected', handleConnected);
        websocketClient.off('disconnected', handleDisconnected);
        websocketClient.off('error', handleError);
        websocketClient.disconnect();
      };
    }
  }, [user]);

  const loadPlanData = async () => {
    try {
      const response = await apiClient.get(`/api/pricing/${planId}`);
      if (response.data && response.data.id) {
        setPlanData(response.data);
        // Generate random payment reference: VOICE{userId}{random4digits}
        if (user) {
          const randomSuffix = Math.floor(1000 + Math.random() * 9000); // 4 digit random
          setPaymentContent(`VOICE${user.id}${randomSuffix}`);
        }
        return true; // Success
      } else {
        message.error('Pricing plan not found');
        return false; // Failed
      }
    } catch (error) {
      console.error('Error loading plan data:', error);
      message.error(error.response?.data?.detail || 'Unable to load plan information');
      return false; // Failed
    }
  };

  const loadUserData = async () => {
    try {
      const response = await apiClient.get('/api/profile/');
      setUserData(response.data);
    } catch (error) {
      console.error('Error loading user data:', error);
      // Don't throw, just log the error
    }
  };

  // Preload QR code images for faster display
  const preloadQRCode = (url, accountId) => {
    // Check cache first
    if (qrCodeCache.current[accountId]) {
      setQrCodeLoaded(prev => ({ ...prev, [accountId]: true }));
      return;
    }
    
    const img = new window.Image(); // Use window.Image to avoid conflict with Ant Design
    img.onload = () => {
      qrCodeCache.current[accountId] = true;
      setQrCodeLoaded(prev => ({ ...prev, [accountId]: true }));
    };
    img.onerror = () => {
      qrCodeCache.current[accountId] = false;
      setQrCodeLoaded(prev => ({ ...prev, [accountId]: false }));
    };
    img.src = url;
  };

  const loadBankAccounts = async () => {
    try {
      const response = await apiClient.get('/api/payment-scanner/sepay/bankaccounts?limit=10');
      
      console.log('API Response:', response.data); // Debug log
      
      if (response.data && response.data.success && response.data.data && response.data.data.length > 0) {
        // Convert format from SePay to display format
        // Sensitive information has already been hidden by the server
        const currentPaymentContent = paymentContent || (user ? `VOICE${user.id}` : 'VOICE4');
        
        const formattedAccounts = response.data.data.map((account, index) => ({
          id: `account_${index}`, // Temporary ID
          bank_name: account.bank_short_name,
          bank_full_name: account.bank_full_name,
          account_number: account.account_number,
          account_name: account.account_holder_name,
          active: true, // Default to active since the server has hidden this field
          bank_code: account.bank_code,
          qr_code: `https://img.vietqr.io/image/${account.bank_code}-${account.account_number}-compact.png?addInfo=${encodeURIComponent(currentPaymentContent)}&amount=${planData?.price || 0}`
        }));
        
        console.log('Formatted accounts:', formattedAccounts); // Debug log
        
        setBankAccounts(formattedAccounts);
        console.log('Bank accounts set:', formattedAccounts); // Debug log
        
        // Preload QR codes after accounts are set (will be updated by useEffect when planData is ready)
        if (planData) {
          formattedAccounts.forEach(account => {
            preloadQRCode(account.qr_code, account.id);
          });
        }
      } else {
        console.warn('SePay API response:', response.data);
        setBankAccounts([]);
      }
    } catch (error) {
      console.error('Error loading bank accounts:', error);
      console.error('Error details:', error.response?.data);
      setBankAccounts([]);
    }
  };

  const checkPaymentStatus = async () => {
    if (checkingPayment || !planData) return;
    
    setCheckingPayment(true);
    try {
      // Check if payment has been processed
      const response = await apiClient.get('/api/payment-scanner/processed-transactions');
      const transactions = response.data.transactions || [];
      
      // Check if there's a recent transaction for this user
      if (user) {
        const userTransaction = transactions.find(t => 
          t.user_id === user.id && 
          t.amount >= planData.price &&
          new Date(t.processed_at) > new Date(Date.now() - 10 * 60 * 1000) // Last 10 minutes
        );
        
        if (userTransaction) {
          setPaymentStatus('completed');
          message.success('Payment successful! Your account has been upgraded.');
          // Refresh user data
          loadUserData();
        }
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    } finally {
      setCheckingPayment(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success('Copied!');
    }).catch(() => {
      message.error('Unable to copy');
    });
  };

  const showQRCode = (bank) => {
    // Generate new payment reference each time the modal opens (random)
    const randomSuffix = Math.floor(1000 + Math.random() * 9000); // 4 digit random
    const currentPaymentContent = user ? `VOICE${user.id}${randomSuffix}` : `VOICE4${randomSuffix}`;
    
    // Update payment content state
    setPaymentContent(currentPaymentContent);
    
    // Update QR code URL with new payment reference
    const qrCodeUrl = `https://img.vietqr.io/image/${bank.bank_code}-${bank.account_number}-compact.png?addInfo=${encodeURIComponent(currentPaymentContent)}&amount=${planData?.price || 0}`;
    
    Modal.info({
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <QrcodeOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
          <span style={{ fontSize: '18px', fontWeight: 600 }}>Payment QR Code - {bank.bank_name}</span>
        </div>
      ),
      content: (
        <div style={{ padding: '0' }}>
          <Card
            style={{
              textAlign: 'center',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: '1px solid #e8e8e8',
              margin: '0 auto'
            }}
            bodyStyle={{ padding: '32px 24px' }}
          >
            {/* QR Code Section - Centered */}
            <div style={{ 
              marginBottom: '24px',
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <div style={{ 
                padding: '20px',
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                border: '2px solid #f0f0f0',
                display: 'inline-block'
              }}>
                <Image
                  src={qrCodeUrl}
                  alt="QR Code"
                  style={{ 
                    width: '300px', 
                    height: '300px',
                    display: 'block',
                    margin: '0 auto',
                    borderRadius: '8px'
                  }}
                  preview={false}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                />
              </div>
            </div>
            
            <Divider style={{ margin: '24px 0', borderColor: '#e8e8e8' }} />
            
            {/* Payment Info Section */}
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '16px 20px',
                backgroundColor: '#f0f7ff',
                borderRadius: '8px',
                border: '1px solid #d4edff'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <DollarOutlined style={{ fontSize: '20px', color: '#52c41a' }} />
                  <Text style={{ fontSize: '15px', color: '#595959', fontWeight: 500 }}>Amount:</Text>
                </div>
                <Text strong style={{ fontSize: '18px', color: '#1890ff', fontWeight: 600 }}>
                  {formatPrice(planData?.price || 0)}
                </Text>
              </div>
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '16px 20px',
                backgroundColor: '#fff7e6',
                borderRadius: '8px',
                border: '1px solid #ffe7ba'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <CreditCardOutlined style={{ fontSize: '20px', color: '#fa8c16' }} />
                  <Text style={{ fontSize: '15px', color: '#595959', fontWeight: 500 }}>Reference:</Text>
                </div>
                <Text 
                  strong 
                  style={{ 
                    fontSize: '18px', 
                    color: '#fa8c16',
                    fontFamily: 'monospace',
                    letterSpacing: '1px',
                    fontWeight: 600
                  }}
                >
                  {currentPaymentContent}
                </Text>
          </div>
            </Space>
            
            {/* Warning Alert */}
            <Alert
              message={<Text strong style={{ fontSize: '15px' }}>Important</Text>}
              description={
                <Text style={{ fontSize: '14px', lineHeight: '1.6' }}>
                  Please transfer the exact amount with the exact reference above so the system can process your payment automatically.
                </Text>
              }
              type="warning"
              showIcon
              style={{ 
                marginTop: '24px', 
                borderRadius: '8px',
                textAlign: 'left'
              }}
            />
          </Card>
        </div>
      ),
      width: 520,
      centered: true,
      okText: 'Got it',
      okButtonProps: {
        style: {
          borderRadius: '6px',
          height: '42px',
          padding: '0 32px',
          fontSize: '15px',
          fontWeight: 500
        }
      },
      style: { paddingBottom: 0 },
      maskStyle: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
    });
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!planData) {
    return null;
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'processing';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'processing': return 'Processing';
      default: return 'Awaiting Payment';
    }
  };

  return (
    <div style={{ margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', textAlign: 'center' }}>
        <Title level={2}>
          <Space>
            <ScanOutlined />
            <span>Payment for {planData.description} Plan</span>
          </Space>
        </Title>
        <Text type="secondary">Automated payment scanning and processing system</Text>
      </div>

      {/* Payment Status */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={16} align="middle">
          <Col span={12}>
            <Statistic
              title="Payment Status"
              value={getStatusText(paymentStatus)}
              valueStyle={{ color: paymentStatus === 'completed' ? '#52c41a' : '#1890ff' }}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            <Button 
              type="primary" 
              icon={<ReloadOutlined />}
              loading={checkingPayment}
              onClick={checkPaymentStatus}
            >
              Check Payment
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Steps */}
      <Card style={{ marginBottom: '24px' }}>
        <Title level={4}>Payment Instructions</Title>
        <Steps
          current={paymentStatus === 'completed' ? 3 : paymentStatus === 'processing' ? 2 : 1}
          items={[
            {
              title: 'Select Plan',
              description: 'Service plan selected',
              icon: <DollarOutlined />
            },
            {
              title: 'Transfer',
              description: 'Transfer with the exact payment reference',
              icon: <BankOutlined />
            },
            {
              title: 'Auto Processing',
              description: 'System auto-scans and upgrades your account',
              icon: <ScanOutlined />
            },
            {
              title: 'Completed',
              description: 'Account has been upgraded',
              icon: <CheckCircleOutlined />
            }
          ]}
        />
      </Card>

      {/* Payment Information */}
      <Row gutter={24}>
        <Col span={12}>
          <Card title="Payment Information" extra={<Tag color="blue">Important</Tag>}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Text strong>Amount: </Text>
                <Text style={{ fontSize: '18px', color: '#1890ff' }}>
                  {formatPrice(planData.price)}
                </Text>
              </div>
              
              <div>
                <Text strong>Transfer Reference: </Text>
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: '8px 12px', 
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#1890ff'
                }}>
                  {paymentContent}
                </div>
                <Button 
                  type="link" 
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(paymentContent)}
                  style={{ marginTop: '8px' }}
                >
                  Copy Reference
                </Button>
              </div>

              <Alert
                message="Important Notice"
                description="You MUST transfer with the exact reference above so the system can automatically identify and upgrade your account. An incorrect reference will not be processed automatically."
                type="warning"
                showIcon
              />
            </Space>
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Bank Accounts">
            {console.log('Rendering bankAccounts:', bankAccounts)} {/* Debug log */}
            <List
              dataSource={bankAccounts}
              locale={{ emptyText: 'Loading bank accounts...' }}
              renderItem={(bank) => (
                <List.Item
                  actions={[
                    <Button 
                      type="primary" 
                      icon={<QrcodeOutlined />}
                      onClick={() => showQRCode(bank)}
                      disabled={!bank.active}
                    >
                      QR Code
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    avatar={<BankOutlined style={{ fontSize: '24px', color: bank.active ? '#1890ff' : '#d9d9d9' }} />}
                    title={
                      <Space>
                        <Text strong>{bank.bank_name}</Text>
                        {bank.active ? (
                          <Tag color="green">Active</Tag>
                        ) : (
                          <Tag color="red">Inactive</Tag>
                        )}
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size="small">
                        <div>
                          <Text strong>Account Number: </Text>
                          <Text copyable={{ text: bank.account_number }}>
                            {bank.account_number}
                          </Text>
                        </div>
                        <div>
                          <Text strong>Account Holder: </Text>
                          <Text>{bank.account_name}</Text>
                        </div>
                        <div>
                          <Text strong>Bank: </Text>
                          <Text>{bank.bank_full_name}</Text>
                        </div>
                        <div>
                          <Text strong>Status: </Text>
                          <Tag color={bank.active ? 'green' : 'red'}>
                            {bank.active ? 'Active' : 'Inactive'}
                          </Tag>
                        </div>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      {/* Current User Status */}
      {userData && (
        <Card style={{ marginTop: '24px' }} title={planData ? "Account Status After Upgrade" : "Current Account Status"}>
          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title="Character Limit"
                value={planData ? (planData.character_limit ?? 0) : (userData.char_limit ?? 0)}
                suffix="chars"
                prefix={<UserOutlined />}
                valueStyle={{ color: planData ? '#52c41a' : '#1890ff' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Used"
                value={userData.used_chars ?? 0}
                suffix="chars"
                prefix={<ClockCircleOutlined />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Remaining"
                value={Math.max(0, (planData ? (planData.character_limit ?? 0) : (userData.char_limit ?? 0)) - (userData.used_chars ?? 0))}
                suffix="chars"
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: planData ? '#52c41a' : '#1890ff' }}
              />
            </Col>
          </Row>
          {planData && (
            <Alert
              message="After successful payment"
              description={`Your character limit will be upgraded from ${(userData.char_limit ?? 0).toLocaleString('vi-VN')} to ${(planData.character_limit ?? 0).toLocaleString('vi-VN')} characters.`}
              type="info"
              showIcon
              style={{ marginTop: '16px', marginBottom: '16px' }}
            />
          )}
          <Progress 
            percent={
              (() => {
                const limit = planData ? (planData.character_limit ?? 0) : (userData.char_limit ?? 0);
                return (limit > 0) 
                  ? Math.round(((userData.used_chars ?? 0) / limit) * 100)
                  : 0;
              })()
            } 
            status={
              (() => {
                const limit = planData ? (planData.character_limit ?? 0) : (userData.char_limit ?? 0);
                return (limit > 0 && (userData.used_chars ?? 0) / limit > 0.8)
                  ? 'exception' 
                  : 'active';
              })()
            }
            style={{ marginTop: planData ? '0' : '16px' }}
          />
        </Card>
      )}

      {/* Actions */}
      <div style={{ marginTop: '24px', textAlign: 'center' }}>
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/pricing')}
          >
            Back
          </Button>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            loading={checkingPayment}
            onClick={checkPaymentStatus}
          >
            Check Payment
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default PaymentScannerPage;